<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import EstimatedTime from '@/components/ui/EstimatedTime.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import TimelineSegment from '@/components/ui/TimelineSegment.vue';
import TimelineStop from '@/components/ui/TimelineStop.vue';
import Common from '@/libs/common';
import type { JourneyLeg, LatLon, TransitLeg, WalkLeg } from '@/libs/journey/journeys';
import { timelineColor } from '@/libs/route-color';
import { useGtfsStore } from '@/stores/gtfs';
import { journeyProgress, timelineRows } from './journey-timeline';
import { formatWalkDistance, formatWalkDuration } from './walk-duration';

/**
 * The steps of the journey being followed, from start to arrival, with the traveller's
 * progress: the steps behind greyed out and, on the ride under way, the stops it calls at.
 */
const props = defineProps<{
  legs: JourneyLeg[];
  /** Epoch seconds */
  now: number;
  position?: LatLon;
  originLabel: string;
  destinationLabel?: string;
}>();

const { t, d, locale } = useI18n();
const gtfsStore = useGtfsStore();

const listEl = ref<HTMLElement>();
const expanded = reactive(new Set<number>());
// A ride unfolds on its own once, as the traveller boards: folding it back is then theirs.
const unfoldedOnBoarding = new Set<number>();

const stopPoint = (stopId: string): LatLon | undefined => {
  const stop = gtfsStore.gtfsData.stops[stopId];
  return stop && { lat: stop.stop_lat, lon: stop.stop_lon };
};
const stopName = (stopId?: string) => (stopId && gtfsStore.gtfsData.stops[stopId]?.stop_name) || '';
const time = (seconds: number) => d(seconds * 1000, 'timeShort');

const progress = computed(() => journeyProgress(props.legs, props.now, props.position, stopPoint));
const rows = computed(() => timelineRows(props.legs, progress.value, expanded));

const ride = (index: number) => props.legs[index] as TransitLeg;
const walk = (index: number) => props.legs[index] as WalkLeg;
const route = (index: number) => gtfsStore.gtfsData.routes[ride(index).routeId];
const lineColor = (index: number) => timelineColor(route(index)?.route_color);

function walkSummary(index: number, transfer: boolean): string {
  const leg = walk(index);
  const values = {
    duration: formatWalkDuration(leg),
    distance: formatWalkDistance(leg.distance, locale.value),
  };
  return t(transfer ? 'itinerary.tracking.transfer' : 'itinerary.tracking.walk', values);
}

function rideSummary(index: number): string {
  const leg = ride(index);
  const stops = leg.stopIds.length - 2;
  return t(
    'itinerary.tracking.stops',
    { n: stops, duration: Common.formatSec(leg.arrival - leg.departure) },
    stops,
  );
}

/** Minutes to wait with live data, the timetable time otherwise. */
function departure(index: number) {
  const leg = ride(index);
  return leg.realtime
    ? {
        type: 'realtime' as const,
        time: Common.formatSec(leg.departure - props.now),
        secondaryTime: time(leg.departure),
      }
    : {
        type: 'theoretical' as const,
        time: time(leg.departure),
        secondaryTime: t('itinerary.tracking.scheduled'),
      };
}

function toggle(index: number) {
  if (expanded.has(index)) expanded.delete(index);
  else expanded.add(index);
}

watch(
  () => (progress.value.lastStop >= 0 ? progress.value.leg : undefined),
  async boardedLeg => {
    if (boardedLeg === undefined || unfoldedOnBoarding.has(boardedLeg)) return;
    unfoldedOnBoarding.add(boardedLeg);
    expanded.add(boardedLeg);
    await nextTick();
    listEl.value?.querySelector('.journey-timeline__position')?.scrollIntoView({ block: 'center' });
  },
  { immediate: true },
);

const timeWidth = ref(0);

// A 12-hour clock (« 12:45 PM ») outgrows the column the design sizes for « 16:04 »: the
// column takes the width of the widest time shown, the same on every row.
function measureTimes() {
  const range = document.createRange();
  const widths = [...(listEl.value?.querySelectorAll('.timeline-stop__time') ?? [])].map(cell => {
    range.selectNodeContents(cell);
    return range.getBoundingClientRect().width;
  });
  timeWidth.value = Math.ceil(Math.max(0, ...widths));
}

watch([rows, locale], () => nextTick(measureTimes));
onMounted(() => {
  measureTimes();
  document.fonts?.ready.then(measureTimes);
});
</script>

<template>
  <div
    ref="listEl"
    class="journey-timeline"
    role="list"
    :style="timeWidth ? { '--timeline-time-width': `${timeWidth}px` } : undefined"
  >
    <template v-for="row in rows" :key="row.key">
      <TimelineStop
        v-if="row.kind === 'origin'"
        :state="row.state"
        :time="time(legs[0].departure)"
        :title="originLabel"
      />

      <TimelineSegment v-else-if="row.kind === 'walk'" :state="row.state" mode="walk">
        <span
          class="journey-timeline__walk"
          :class="{ 'journey-timeline__walk--past': row.state === 'past' }"
        >
          <BaseIcon name="footprints" class="journey-timeline__walk-icon" />
          {{ walkSummary(row.leg, row.transfer) }}
        </span>
      </TimelineSegment>

      <TimelineStop
        v-else-if="row.kind === 'board'"
        :color="lineColor(row.leg)"
        :detail="
          row.state === 'past'
            ? $t('itinerary.tracking.boarded', { time: time(ride(row.leg).departure) })
            : $t('itinerary.tracking.board')
        "
        :state="row.state"
        :time="time(ride(row.leg).departure)"
        :title="stopName(ride(row.leg).boardStopId)"
        line-below
      />

      <TimelineSegment
        v-else-if="row.kind === 'ride'"
        :color="lineColor(row.leg)"
        :state="row.state"
        class="journey-timeline__ride"
        :class="{ 'journey-timeline__ride--past': row.state === 'past' }"
        mode="ride"
      >
        <div class="journey-timeline__line">
          <span class="journey-timeline__route">
            <RouteBadge v-if="route(row.leg)" :route="route(row.leg)" size="small" />
            <span class="journey-timeline__headsign">→ {{ ride(row.leg).headsign }}</span>
          </span>
          <EstimatedTime
            v-if="!row.boarded"
            v-bind="departure(row.leg)"
            class="journey-timeline__departure"
          />
        </div>
        <button
          v-if="ride(row.leg).stopIds.length > 2"
          :aria-expanded="row.expanded"
          class="journey-timeline__summary journey-timeline__summary--toggle"
          type="button"
          @click="toggle(row.leg)"
        >
          {{ rideSummary(row.leg) }}
          <BaseIcon
            name="chevron-down"
            class="journey-timeline__chevron"
            :class="{ 'journey-timeline__chevron--open': row.expanded }"
          />
        </button>
        <span v-else class="journey-timeline__summary">{{ rideSummary(row.leg) }}</span>
      </TimelineSegment>

      <TimelineStop
        v-else-if="row.kind === 'stop'"
        :color="lineColor(row.leg)"
        :state="row.state"
        :time="time(ride(row.leg).stopTimes[row.stop])"
        :title="stopName(ride(row.leg).stopIds[row.stop])"
        line-above
        line-below
        type="intermediate"
      />

      <TimelineSegment
        v-else-if="row.kind === 'position'"
        :color="lineColor(row.leg)"
        class="journey-timeline__position"
        mode="ride"
        state="current"
      >
        <span class="journey-timeline__summary">
          {{ $t('itinerary.tracking.nextStop', { stop: stopName(ride(row.leg).stopIds[row.nextStop]) }) }}
        </span>
      </TimelineSegment>

      <TimelineStop
        v-else-if="row.kind === 'alight'"
        :color="lineColor(row.leg)"
        :detail="
          row.final
            ? $t('itinerary.tracking.destination')
            : row.leg === progress.leg && progress.lastStop >= 0
              ? $t('itinerary.tracking.alightHere')
              : $t('itinerary.tracking.alight')
        "
        :marker="row.final ? 'filled' : 'ring'"
        :state="row.state"
        :time="time(ride(row.leg).arrival)"
        :title="row.final && destinationLabel ? destinationLabel : stopName(ride(row.leg).alightStopId)"
        line-above
      />

      <TimelineStop
        v-else-if="row.kind === 'destination'"
        :detail="$t('itinerary.tracking.destination')"
        :state="row.state"
        :time="time(legs[legs.length - 1].arrival)"
        :title="destinationLabel ?? stopName(walk(legs.length - 1).toStopId)"
        marker="filled"
      />
    </template>
  </div>
</template>

<style lang="scss">
.journey-timeline {
  display: flex;
  flex-direction: column;
  padding: $spacing-4;

  &__walk {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    font: $text-smaller;
    gap: $spacing-1;
    letter-spacing: $text-smaller-spacing;

    &--past {
      color: $color-neutral-high;
    }
  }

  &__walk-icon {
    flex-shrink: 0;
    font-size: 16px;
  }

  &__line {
    align-items: flex-start;
    display: flex;
    gap: $spacing-2;
    justify-content: space-between;
  }

  &__route {
    align-items: center;
    display: flex;
    gap: $spacing-2;
    min-width: 0;
    padding-top: 2px;
  }

  &__headsign {
    color: $color-neutral-highest;
    font: $text-small;
    letter-spacing: $text-small-spacing;
  }

  &__departure {
    flex-shrink: 0;
  }

  &__summary {
    align-items: center;
    align-self: flex-start;
    color: $color-neutral-higher;
    display: inline-flex;
    font: $text-smaller;
    gap: $spacing-1;
    letter-spacing: $text-smaller-spacing;

    &--toggle {
      background: none;
      border: 0;
      cursor: pointer;
      padding: 0;
    }
  }

  &__chevron {
    font-size: 16px;
    transition: transform 0.2s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__ride--past {
    .journey-timeline__headsign,
    .journey-timeline__summary {
      color: $color-neutral-high;
    }
  }
}
</style>
